import "./siteWorkflows.css"
import { Alert, Button, Card, Col, Descriptions, Row, Select, Space, Tag, Typography } from "antd"
import { useEffect, useState } from "react"
import { siteWorkflowApi } from "../../utils/siteWorkflowApi"
import { differenceLabels } from "../../types/siteWorkflows"
import { useWorkflowResource } from "./useWorkflowResource"
import { FindingSummary, AnswerFollowUpButton } from "./CorrectiveActions"
import { WorkflowError, WorkflowPager } from "./WorkflowCommon"
export function InspectionComparisonPanel({ site }: { site: string }) {
  const [historyPage, setHistoryPage] = useState(1)
  const history = useWorkflowResource(`comparison-inspections/${site}/${historyPage}`, (signal) =>
    siteWorkflowApi.inspections(site, historyPage, signal),
  )
  const [choices, setChoices] = useState<{ value: string; label: string }[]>([])
  useEffect(() => {
    if (history.data)
      setChoices((current) => {
        const map = new Map(current.map((v) => [v.value, v]))
        for (const i of history.data!.items)
          map.set(i.uuid, {
            value: i.uuid,
            label: `${i.executionDate?.slice(0, 10) ?? "Sans date"} · ${i.missionName || i.siteName}`,
          })
        return Array.from(map.values())
      })
  }, [history.data])
  const [before, setBefore] = useState(""),
    [after, setAfter] = useState("")
  const [selection, setSelection] = useState<{ before: string; after: string }>()
  const [kind, setKind] = useState(""),
    [page, setPage] = useState(1)
  const result = useWorkflowResource(
    selection ? `comparison/${site}/${selection.before}/${selection.after}/${kind}/${page}` : "",
    (signal) =>
      siteWorkflowApi.compare(site, selection!.before, selection!.after, kind, page, signal),
  )
  const data = result.data
  return (
    <Space className="site-workflow-panel" direction="vertical" size="middle" style={{ width: "100%" }}>
      <Alert
        type="info"
        message="Comparez deux inspections EN 1176 du même site. Les identités d’équipement et de composant doivent être rattachées. Un contrôle retiré, sans réponse ou dont la définition a changé ne constitue pas une anomalie résolue."
      />
      <Space wrap>
        <Select
          aria-label="Inspection précédente"
          placeholder="Inspection précédente"
          showSearch
          optionFilterProp="label"
          value={before || undefined}
          onChange={setBefore}
          options={choices}
          style={{ width: "min(100%, 350px)", minWidth: 220 }}
        />
        <Select
          aria-label="Inspection suivante"
          placeholder="Inspection suivante"
          showSearch
          optionFilterProp="label"
          value={after || undefined}
          onChange={setAfter}
          options={choices}
          style={{ width: "min(100%, 350px)", minWidth: 220 }}
        />
        <Button
          type="primary"
          disabled={!before || !after || before === after}
          onClick={() => {
            setSelection({ before, after })
            setKind("")
            setPage(1)
          }}
        >
          Comparer
        </Button>
        {historyPage * 50 < (history.data?.total ?? 0) && (
          <Button onClick={() => setHistoryPage((n) => n + 1)}>
            Charger des inspections plus anciennes
          </Button>
        )}
      </Space>
      <WorkflowError error={history.error} retry={history.reload} />
      <WorkflowError error={result.error} retry={result.reload} />
      {data && (
        <>
          <Row gutter={[12, 12]}>
            {[data.before, data.after].map((s, index) => (
              <Col xs={24} md={12} key={s.inspectionUuid}>
                <Card
                  size="small"
                  title={`${index ? "Après" : "Avant"} · ${s.missionName || s.siteName}`}
                >
                  <Tag color={s.frozen ? "green" : "orange"}>
                    {s.frozen ? "Photographie à la clôture" : "Données actuelles modifiables"}
                  </Tag>
                  <div>Capture : {new Date(s.capturedAt).toLocaleString("fr-FR")}</div>
                  {s.warnings.map((w) => (
                    <Alert key={w} type="warning" message={w} />
                  ))}
                </Card>
              </Col>
            ))}
          </Row>
          <Space wrap>
            {Object.entries(data.counts).map(([k, n]) => (
              <Tag key={k}>
                {differenceLabels[k] || k} : {n}
              </Tag>
            ))}
          </Space>
          <Space wrap>
            <Select
              aria-label="Filtrer les différences"
              value={kind}
              style={{ minWidth: 240 }}
              onChange={(v) => {
                setKind(v)
                setPage(1)
              }}
              options={[
                { value: "", label: "Tous les résultats" },
                ...Object.entries(differenceLabels).map(([value, label]) => ({ value, label })),
              ]}
            />
            <Button onClick={result.reload}>Actualiser la comparaison</Button>
          </Space>
          {data.items.map((d) => {
            const finding = d.after ?? d.before!
            return (
              <Card
                size="small"
                key={d.key}
                title={
                  <Space wrap>
                    {finding.category?.iconKey && (
                      <img
                        width={28}
                        height={28}
                        alt=""
                        src={`/icones/${finding.category.iconKey}.png`}
                      />
                    )}
                    <span>
                      {finding.category?.label ?? "Contrôles"} · {finding.equipmentName || "Site"}
                      {finding.contextLabel ? ` · ${finding.contextLabel}` : ""}
                    </span>
                    <Tag
                      color={
                        d.kind === "improved" ? "green" : d.kind === "worsened" ? "red" : "default"
                      }
                    >
                      {differenceLabels[d.kind] || d.kind}
                    </Tag>
                  </Space>
                }
              >
                <Row gutter={[16, 16]}>
                  {[
                    { label: "Avant", f: d.before },
                    { label: "Après", f: d.after },
                  ].map(({ label, f }) => (
                    <Col xs={24} md={12} key={label}>
                      <Typography.Title level={5}>{label}</Typography.Title>
                      {f ? (
                        <>
                          <FindingSummary finding={f} />
                          <Descriptions
                            size="small"
                            column={1}
                            items={[
                              {
                                key: "revision",
                                label: "Révision",
                                children: f.revisionUuid || "Non connue",
                              },
                            ]}
                          />
                          {f.answerUuid && <AnswerFollowUpButton answerUuid={f.answerUuid} />}
                        </>
                      ) : (
                        <Typography.Text type="secondary">
                          Absent du périmètre enregistré
                        </Typography.Text>
                      )}
                    </Col>
                  ))}
                </Row>
              </Card>
            )
          })}
          <WorkflowPager page={page} total={data.total} onChange={setPage} />
        </>
      )}
    </Space>
  )
}
